import { useSelectorUserInfo } from '@/components/auth/AuthContext';

/**
 * The signed-in user's own role, read straight from `AuthContext`.
 *
 * Deliberately not `useUserRole`, even though that hook answers the same
 * question. `useUserRole` calls `/me` and then `setCurrentUser` whenever the
 * context is still empty, and `AuthProvider` builds a fresh `value` object on
 * every render - so each extra instance of that hook pushes another context
 * update through every consumer below it.
 *
 * That is harmless in a route guard, which mounts once. It is not harmless in
 * the chat dialogs: `AddMemberGroupDialog` re-seeds `selectedUsers` from
 * `allMembers` on every identity change of that array, so the extra renders
 * wiped a member selection between picking someone and pressing the button.
 * Nothing in the dialog looked wrong - the button simply went back to disabled.
 *
 * Reading the context is safe here because `RoleProtectedRoute` holds a
 * `Loader` until the role resolves, so no chat component mounts before
 * `currentUser` is populated.
 */
export default function useCurrentRole() {
  const { currentUser } = useSelectorUserInfo();
  const roles = currentUser?.current_roles;

  return {
    role: roles?.[0] ?? '',
    // Empty until `/me` lands. No default role is safe in both directions -
    // guessing TEACHER costs an admin its mentors, guessing anything else
    // leaks them to a coach - so callers wait rather than guess.
    loaded: Boolean(roles?.length),
  };
}
